import { _ } from "../../polyglot/polyglot.js";
import {
	downloadIcon,
	eyeIcon,
	pencilSolidIcon,
	plusIcon,
	timesIcon,
	trashAltIcon,
	undoIcon,
	uploadIcon,
} from "../../elements/icons/index.js";
import { iconAndText } from "../../elements/index.js";
import { none } from "../../elements/size/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../list/streams/ArrayStream.js";
import ButtonDropdown from "../ButtonDropdown/ButtonDropdown.js";
import FileSelectionButtonWidget from "../FileSelectionButtonWidget.js";
import GridColumnSpecification from "../list/GridColumnSpecification.js";
import KeyFigureSelectionWidget from "../keyFigures/KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import KeyFigureTypeChoiceWidget from "../keyFigures/KeyFigureTypeChoiceWidget/KeyFigureTypeChoiceWidget.js";
import ListSource from "../../list/ListSource.js";
import makeTableListWidget from "../list/TableListWidget/makeTableListWidget.js";
import SearchFieldWidget from "../SearchFieldWidget/SearchFieldWidget.js";
import SwitchButton from "../SwitchButton/SwitchButton.js";

/**
 * `VisibleKeyFigureListWidget` renders a grid of visible key figures
 * with buttons to create more key figures and a search bar. The
 * widget maintains a list of non-visible key figures that can be made
 * visible by clicking a dedicated "Add KPIs from list" button.
 *
 * @param {function} changeVisibleKeyFiguresCallback - Called with no
 * argument when the lists of visible and invisible key figures change
 * as a result of a user action.
 * @param {function} [createKeyFigureCallback] - Called with no
 * argument when the user clicks the corresponding button. The button
 * is only shown when a function is provided.
 * @param {function} [editKeyFigureCallback] - Called with a key
 * figure as argument when the user clicks the "Edit" button next to a
 * key figure. The "Edit" button is only shown when a function is
 * provided.
 * @param {function} [removeKeyFigureCallback] - Called with a key
 * figure as argument when the user clicks the "Delete" button next to
 * a key figure. This argument is optional and the "Delete" button is
 * only shown when a function is provided. The "Delete" button is also
 * only visible if the key figure can be deleted (see
 * `canDeleteKeyFigure`).
 * @param {function} [canDeleteKeyFigure] - Called with a key figure
 * as argument and should return true if the "Delete" button is to be
 * displayed for this key figure.
 * @param {function} [viewKeyFigureCallback] - Called with a key
 * figure as argument when the user clicks the "View" button next to
 * a key figure. This argument is optional and the "View" button is
 * only shown when a function is provided.
 * @param {function():boolean} [canEditKeyFigures] - Invoked with no arguments
 *   to know if this widget can handle kpi edition (including creation)
 * @param {function} [isKeyFigureEnabled] - Called with a key figure
 * as argument. The return value represents the state of the
 * enable/disable toggle. This function must be provided for the
 * enable/disable column to be visible.
 * @param {function} [setKeyFigureEnabled] - Called with a key figure
 * and a boolean as argument. The boolean is true if the key figure
 * must be enabled.
 * @param {function} [canDisableKeyFigure] - Called with a key figure
 * as argument. Must return true for the toggle to appear for this key
 * figure.
 * @param {object} [customKPI] - Object parameter holding all the params
 * related to the handling of custom KPIs.
 * @param {function} customKPI.createNewCustomKeyFigure - Function called when a new custom
 * KPI is imported. This function should return a promise. This promise should resolve with the newly created key figure.
 * @param {function} customKPI.updateExistingKeyFigure - Function called when a new custom
 * KPI is imported and it's replacing an existing one. This function should return a promise. This promise should resolve with
 * the newly imported key figure.
 * @param {function} customKPI.restoreKeyFigure - Function called when the restore action
 * is triggered. This function should return a promise. This promise should resolve with the restored key figure.
 * @param {function} customKPI.canDeleteKeyFigure - Predicate returning true when the custom keyFigure provided
 * as argument can be deleted.
 * @param {function} customKPI.canRestoreKeyFigure - Predicate returning true when the custom keyFigure provided
 * as argument can be restored.
 * @param {function} customKPI.canUploadKeyFigure - Predicate returning true when the custom keyFigure provided
 * as argument can be uploaded.
 * @param {function} customKPI.canDownloadKeyFigure - Predicate returning true when the custom keyFigure provided
 * as argument can be downloaded.
 * @param {function} customKPI.getDownloadUrlForKeyFigure - Function returning the URL to download the keyFigure provided
 * as argument.
 */
export default class VisibleKeyFigureListWidget extends Widget {
	constructor({
		changeVisibleKeyFiguresCallback,
		createKeyFigureCallback,
		editKeyFigureCallback,
		removeKeyFigureCallback,
		viewKeyFigureCallback,
		canEditKeyFigures = (_keyFigure) => false,
		canDeleteKeyFigure = (_keyFigure) => false,
		isKeyFigureEnabled,
		setKeyFigureEnabled,
		canDisableKeyFigure = (_keyFigure) => false,
		customKPI,
	} = {}) {
		super(...arguments);

		this._changeVisibleKeyFiguresCallback = changeVisibleKeyFiguresCallback;
		this._createKeyFigureCallback = createKeyFigureCallback;
		this._editKeyFigureCallback = editKeyFigureCallback;
		this._removeKeyFigureCallback = removeKeyFigureCallback;
		this._viewKeyFigureCallback = viewKeyFigureCallback;
		this._canEditKeyFigures = canEditKeyFigures;
		this._canDeleteKeyFigure = canDeleteKeyFigure;
		this._isKeyFigureEnabled = isKeyFigureEnabled;
		this._setKeyFigureEnabled = setKeyFigureEnabled;
		this._canDisableKeyFigure = canDisableKeyFigure;
		this._customKPI = customKPI;

		this._grid = makeTableListWidget({
			columnSpecifications: this._gridColumns(),
		});

		this._searchField = new SearchFieldWidget({
			inputId: "keyfigure-search",
			inputClass: "form-control search-field",
		});
	}

	setKeyFigures({ invisibleKeyFigures = [], visibleKeyFigures = [] }) {
		this._invisibleKeyFigures = invisibleKeyFigures;
		this._visibleKeyFigures = visibleKeyFigures;

		this._updateGridData();
	}

	getVisibleKeyFigures() {
		return this._visibleKeyFigures;
	}

	getInvisibleKeyFigures() {
		return this._invisibleKeyFigures;
	}

	renderContentOn(html) {
		html.div(
			{ class: "row grid-actions", "data-test": "grid-actions" },
			this._renderSearchFieldOn.bind(this),
			this._renderActionsOn.bind(this),
		);

		html.div({ "data-test": "kpis" }, this._grid);
	}

	_renderSearchFieldOn(html) {
		html.div(
			{
				class: "col col-sm-12 col-md-6",
			},
			this._searchField,
		);
	}

	_renderActionsOn(html) {
		html.div(
			{ class: "col col-sm-12 col-md-6" },
			html.div(
				{ class: "pull-right" },
				this._renderClearKeyFiguresButtonOn.bind(this),
				this._renderMakeKeyFiguresVisibleButtonOn.bind(this),
				this._renderCreateKeyFigureButtonOn.bind(this),
				this._renderImportKeyFigureButtonOn.bind(this),
			),
		);
	}

	_renderClearKeyFiguresButtonOn(html) {
		html.button(
			{
				"data-test": "clear",
				class: "btn btn-default icon-and-text clear-filters",
				click: () => this._hideAllKeyFigures(),
			},
			trashAltIcon({ class: "fa-large" }),
			_("Clear KPIs"),
		);
	}

	_renderMakeKeyFiguresVisibleButtonOn(html) {
		const additionalCssClass =
			this._shouldDisplayCreateKeyFigureButton() ||
			this._shouldDisplayImportKeyFigureButton()
				? "btn btn-default"
				: "btn btn-primary";

		html.button(
			{
				"data-test": "add",
				class: ["btn", "icon-and-text", additionalCssClass],
				click: () => this._showKeyFiguresToMakeVisibleDialog(),
			},
			plusIcon({ class: "fa-large" }),
			_("Add KPIs from list"),
		);
	}

	_renderCreateKeyFigureButtonOn(html) {
		if (!this._shouldDisplayCreateKeyFigureButton()) {
			return;
		}

		html.button(
			{
				click: this._createKeyFigureCallback,
				class: "btn btn-primary",
				"data-test": "create-custom-kpi",
			},
			_("New custom KPI"),
		);
	}

	_renderImportKeyFigureButtonOn(html) {
		if (!this._shouldDisplayImportKeyFigureButton()) {
			return;
		}

		html.render(
			FileSelectionButtonWidget.forJSONFile({
				label: _("Import custom KPI"),
				fileSelectedCallback: this._showCreationDialog.bind(this),
				cssClass: "btn btn-primary",
				dataTestName: "import-custom-kpi",
			}),
		);
	}

	_keyFigureActionsRenderer(keyFigure) {
		return new ButtonDropdown({
			elements: this._createKeyFigureActions(keyFigure),
			alignRight: true,
			dataTestName: "kpi-actions",
		});
	}

	_keyFigureToggleEnabledRenderer(keyFigure) {
		if (!this._canDisableKeyFigure(keyFigure)) {
			return () => {};
		}

		const switchButton = new SwitchButton({
			dataTestName: "toggle-enable",
			state: this._isKeyFigureEnabled(keyFigure),
		});

		switchButton.stateChanged.register((state) =>
			this._setKeyFigureEnabled(keyFigure, state),
		);

		return switchButton;
	}

	_createBaseKeyFigureActions({ keyFigure, isCustom = false }) {
		const keyFigureActions = [];

		if (this._canEditKeyFigures()) {
			keyFigureActions.push({
				label: _("Edit"),
				icon: pencilSolidIcon.getCssClass(),
				action: () => this._editKeyFigureCallback(keyFigure),
				dataTestName: isCustom ? "edit-custom-kpi" : "edit-kpi",
			});
		}

		if (this._viewKeyFigureCallback) {
			keyFigureActions.push({
				label: _("View"),
				icon: eyeIcon.getCssClass(),
				action: () => this._viewKeyFigureCallback(keyFigure),
				dataTestName: isCustom ? "view-custom-kpi" : "view-kpi",
			});
		}

		keyFigureActions.push({
			label: _("Remove from visible"),
			icon: timesIcon.getCssClass(),
			action: () => this._hideKeyFigure(keyFigure),
			dataTestName: isCustom ? "hide-custom-kpi" : "hide-kpi",
		});

		return keyFigureActions;
	}

	_createCustomKeyFigureActions(keyFigure) {
		const keyFigureActions = this._createBaseKeyFigureActions({
			keyFigure,
			isCustom: true,
		});

		const canDeleteKeyFigure =
			(this._customKPI &&
				this._customKPI.canDeleteKeyFigure(keyFigure)) ||
			(this._removeKeyFigureCallback &&
				this._canDeleteKeyFigure(keyFigure));

		if (canDeleteKeyFigure) {
			keyFigureActions.push({
				label: _("Delete"),
				icon: trashAltIcon.getCssClass(),
				action: this._removeKeyFigure.bind(this, keyFigure),
				dataTestName: "delete-custom-kpi",
			});
		}

		if (!this._customKPI) {
			return keyFigureActions;
		}

		if (this._customKPI.canRestoreKeyFigure(keyFigure)) {
			keyFigureActions.push({
				label: _("Restore"),
				icon: undoIcon.getCssClass(),
				action: this._restore.bind(this, keyFigure),
				dataTestName: "restore-custom-kpi",
			});
		}

		if (this._customKPI.canUploadKeyFigure(keyFigure)) {
			keyFigureActions.push(
				FileSelectionButtonWidget.forJSONFile({
					cssClass: "icon-and-text",
					dataTestName: "upload-custom-kpi",
					label: iconAndText(
						{ space: none, icon: uploadIcon },
						_("Upload"),
					),
					fileSelectedCallback: (json) => {
						this._updateExistingKeyFigure({
							keyFigureId: keyFigure.getId(),
							keyFigureData: json,
						});
					},
				}),
			);
		}

		if (this._customKPI.canDownloadKeyFigure(keyFigure)) {
			keyFigureActions.push({
				download: `${keyFigure.getName()}.json`,
				label: _("Download"),
				icon: downloadIcon.getCssClass(),
				href: this._customKPI.getDownloadUrlForKeyFigure(keyFigure),
				dataTestName: "download-custom-kpi",
			});
		}

		return keyFigureActions;
	}

	_createKnownKeyFigureActions(keyFigure) {
		const keyFigureActions = this._createBaseKeyFigureActions({
			keyFigure,
		});

		if (
			this._removeKeyFigureCallback &&
			this._canDeleteKeyFigure(keyFigure)
		) {
			keyFigureActions.push({
				label: _("Delete"),
				icon: trashAltIcon.getCssClass(),
				action: () => this._removeKeyFigure(keyFigure),
				dataTestName: "delete-kpi",
			});
		}
		return keyFigureActions;
	}

	_createKeyFigureActions(keyFigure) {
		if (keyFigure.hasBeenModified()) {
			return this._createCustomKeyFigureActions(keyFigure);
		}

		return this._createKnownKeyFigureActions(keyFigure);
	}

	_keyFigureTitle(keyFigure) {
		const newName = keyFigure.getName();
		const knownName = keyFigure.getKnownName();

		if (!newName) {
			return knownName || _("<no name>");
		}

		if (!knownName || !keyFigure.isRenamed()) {
			return newName;
		}

		return [
			newName,
			" ",
			(html) =>
				html.small(
					_("(formerly named '{knownName}')", {
						knownName,
					}),
				),
		];
	}

	_restore(keyFigure) {
		const name = this._keyFigureTitle(keyFigure);

		this._confirmDeletion({
			title: _("Restore {name}?", { name }),
			body: _(
				"Are you sure you want to permanently restore the uploaded '{name}' and go back to its original version?",
				{ name },
			),
			callback: () =>
				this._customKPI
					.restoreKeyFigure(keyFigure)
					.then((restoredKeyFigure) => {
						this._makeKeyFiguresVisible([restoredKeyFigure]);
					}),
		});
	}

	_updateGridData() {
		const listSource = ListSource.makeAndFetch({
			stream: new ArrayStream({ data: this._visibleKeyFigures }),
		});

		this._grid.setSource(listSource);
		this._searchField.applySearchOn(listSource);
	}

	_hideKeyFigure(keyFigureToHide) {
		this._changeVisibleKeyFigures({
			visibleKeyFigures: this._visibleKeyFigures.filter(
				(visibleKeyFigure) => visibleKeyFigure !== keyFigureToHide,
			),
			invisibleKeyFigures: [
				...this._invisibleKeyFigures,
				keyFigureToHide,
			],
		});
	}

	_hideAllKeyFigures() {
		this._changeVisibleKeyFigures({
			visibleKeyFigures: [],
			invisibleKeyFigures: [
				...this._invisibleKeyFigures,
				...this._visibleKeyFigures,
			],
		});
	}

	_makeKeyFiguresVisible(keyFiguresToMakeVisible) {
		const isNotInKeyFiguresToMakeVisible = (each) =>
			keyFiguresToMakeVisible.every(
				(keyFigureToAdd) => keyFigureToAdd.getId() !== each.getId(),
			);

		const visibleKeyFigures = this._visibleKeyFigures.filter(
			isNotInKeyFiguresToMakeVisible,
		);

		const invisibleKeyFigures = this._invisibleKeyFigures.filter(
			isNotInKeyFiguresToMakeVisible,
		);

		this._changeVisibleKeyFigures({
			visibleKeyFigures: [
				...keyFiguresToMakeVisible,
				...visibleKeyFigures,
			],
			invisibleKeyFigures,
		});
	}

	_changeVisibleKeyFigures({ visibleKeyFigures, invisibleKeyFigures }) {
		this.setKeyFigures({ visibleKeyFigures, invisibleKeyFigures });

		this._changeVisibleKeyFiguresCallback({
			visibleKeyFigures,
			invisibleKeyFigures,
		});
	}

	_removeKeyFigure(keyFigure) {
		this._confirmDeletion({
			name: keyFigure.getName(),
			callback: () => this._removeKeyFigureCallback(keyFigure),
		});
	}

	_gridColumns() {
		const firstColumns = [
			new GridColumnSpecification({
				name: _("Name"),
				sortCriterion: "name",
				renderer: this._keyFigureTitle.bind(this),
				cssClass: "wrappable",
			}),
			new GridColumnSpecification({
				name: _("Group"),
				sortCriterion: "group",
				renderer: (keyFigure) => keyFigure.getGroup(),
				cssClass: "wrappable",
			}),
		];

		const actionColumn = new GridColumnSpecification({
			name: _("Actions"),
			renderer: this._keyFigureActionsRenderer.bind(this),
		});

		const enableColumn = new GridColumnSpecification({
			name: _("Enabled/Disabled"),
			headerCssClass: "text-center",
			cssClass: "text-center",
			renderer: this._keyFigureToggleEnabledRenderer.bind(this),
		});

		let columns;

		if (this._isKeyFigureEnabled) {
			columns = [...firstColumns, enableColumn, actionColumn];
		} else {
			columns = [...firstColumns, actionColumn];
		}

		return columns;
	}

	_shouldDisplayCreateKeyFigureButton() {
		return this._canEditKeyFigures() && !!this._createKeyFigureCallback;
	}

	_shouldDisplayImportKeyFigureButton() {
		return !!this._customKPI;
	}

	_showCreationDialog(keyFigureData) {
		const choiceWidget = new KeyFigureTypeChoiceWidget({
			allKeyFigures: [
				...this._visibleKeyFigures,
				...this._invisibleKeyFigures,
			],
		});

		this._confirm({
			title: _("Create new KPI"),
			okLabel: _("Create new KPI"),
			contentCssClass: "resizable-modal-content",
			body: choiceWidget,
			callback: () => {
				choiceWidget.hasSelectedNewCustomKeyFigure()
					? this._createNewCustomKeyFigure(keyFigureData)
					: this._updateExistingKeyFigure({
							keyFigureId: choiceWidget
								.selectedKeyFigure()
								.getId(),
							keyFigureData,
						});
			},
		});
	}

	_updateExistingKeyFigure({ keyFigureId, keyFigureData }) {
		keyFigureData.id = keyFigureId;

		this._customKPI
			.updateExistingKeyFigure(keyFigureData)
			.then((keyFigure) => this._makeKeyFiguresVisible([keyFigure]));
	}

	_createNewCustomKeyFigure(keyFigureData) {
		this._customKPI
			.createNewCustomKeyFigure(keyFigureData)
			.then((keyFigure) => this._makeKeyFiguresVisible([keyFigure]));
	}

	_showKeyFiguresToMakeVisibleDialog() {
		const keyFigurePicker = new KeyFigureSelectionWidget({
			multipleSelection: true,
			keyFigures: [...this._invisibleKeyFigures],
		});

		this._confirm({
			title: _("Select KPIs to make visible"),
			body: keyFigurePicker,
			okLabel: _("Make visible"),
			contentCssClass: "resizable-modal-content",
			extraButtonsRenderer: () => [
				(html) =>
					html.a(
						{
							class: "btn btn-link",
							"data-test": "select-all",
							click: () => keyFigurePicker.selectAll(),
						},
						_("Select all"),
					),
			],
			callback: () =>
				this._makeKeyFiguresVisible(keyFigurePicker.getSelectedItems()),
		});
	}
}
